// Imports => Vendor
import axios from 'axios';

// Imports => MOBX
import {
  makeObservable,
  observable,
  computed,
  action,
  runInAction,
} from 'mobx';

// Imports => Constants
import { KEYS } from '@opp/ui-kit';

// Imports => Models
import { TransactionModel } from '@models/transaction.model';

// Imports => Utilities
import {
  AcSanitize,
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcFormatErrorMessage,
  AcFormatErrorCode,
  AcIsUndefined,
  AcFormatRequestParameters,
} from '@opp/ui-kit';
import { AcAutoLoad, AcAutoSave } from '@utils';

const _default = {
  options: {},
  meta: null,
  purchases: null,
  purchase: null,
};

let app = {};

export class PurchasesStore {
  constructor(store) {
    makeObservable(this);

    AcAutoLoad(this, KEYS.PURCHASES);
    AcAutoSave(this);

    app.store = store;
  }

    @observable
      meta = _default.meta;

    @observable
      purchases = _default.purchases;

    @observable
      purchase = _default.purchase;

    @computed
    get current_meta() {
      return this.meta || [];
    }

    @computed
    get current_purchases() {
      return this.purchases || [];
    }

    @computed
    get current_purchase() {
      return this.purchase;
    }

    @observable
      loading = {
        status: false,
        message: null,
      };

    @computed
    get is_loading() {
      return this.loading.status;
    }

    @action
      setLoading = (state, message) => {
        this.loading = {
          status: state || false,
          message,
        };
      };

    @observable
      busy = {
        status: false,
        message: null,
      };

    @computed
    get is_busy() {
      return this.busy.status;
    }

    @action
      setBusy = (state, message) => {
        this.busy = {
          status: state || false,
          message,
        };
      };

    @observable
      order_by = {
        field: 'date',
        direction: KEYS.ASCENDING,
      };

    @observable
      page = 1;

    @observable
      perpage = 15;

    @observable
      query = null;

    @action
      setQuery = (input) => {
        const _input = AcSanitize(input);
        if (this.current_query !== _input) this.query = _input;
      };

    @action
      setPageNumber = (num) => {
        if (this.page !== num) this.page = num;
      };

    @action
      setPerPage = (num) => {
        if (this.perpage !== num) this.perpage = num;
      };

    @action
      setOrderBy = (field, direction = null) => {
        let order_by = this.order_by;

        if (AcIsSet(direction)) {
          order_by.direction = direction;
        } else if (order_by.field === field) {
          order_by.direction =
                order_by.direction === KEYS.ASCENDING
                  ? KEYS.DESCENDING
                  : KEYS.ASCENDING;
        } else order_by.direction = KEYS.ASCENDING;

        order_by.field = field;

        this.order_by = order_by;
        this.setPageNumber(1);
      };

    @computed
    get current_page() {
      return this.page;
    }

    @computed
    get current_per_page() {
      return this.perpage;
    }

    @computed
    get current_order_by() {
      return this.order_by;
    }

    @computed
    get current_query() {
      return this.query;
    }

    @action
      resetParams = () => {
        this.page = 1;
        this.query = null;
        this.perpage = 25;
        this.order_by = { field: null, direction: KEYS.ASCENDING };
      };

    @action
      list = (t, options = {}) => {
        this.setLoading(true);

        const params = AcFormatRequestParameters(this, options);

        return app.store.api.purchases
          .list(params)
          .then(async (response) => {
            const collection = response.data;
            const len = collection.length;
            let n = 0;
            let result = [];

            let uids = collection.map(purchase => purchase.uid);

            const parentPaymentMethods = await app.store.api.movements.get_parent_payment_methods(uids).catch((error) => {
              // Payment method probably shows multi. Not the biggest issue
              console.log(error);
            });

            for (n; n < len; n++) {
              const item = collection[n];
              let parent_payment_method = null;
              parentPaymentMethods.every((parentPaymentMethod) => {
                if (item.uid === parentPaymentMethod.uid) {
                  parent_payment_method = parentPaymentMethod.parent_payment_method;
                  return false;
                }

                return true;
              });

              const formatted = new TransactionModel(item, parent_payment_method);
              result.push(formatted);
            }

            await this.set(KEYS.PURCHASES, result);

            const meta = response.meta;
            runInAction(() => {
              this.meta = meta;
            });

            this.setLoading(false);

            return result;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('purchases.failed-to-retrieve', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            runInAction(() => {
              this.meta = null;
            });

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      get_by_id = (t, id, options) => {
        this.setLoading(true);

        const params = AcFormatRequestParameters(this, options, true);

        return app.store.api.purchases
          .get_by_id(id, params)
          .then(async (response) => {
            let parent_payment_method = null;

            await app.store.api.movements.get_parent_payment_methods([response.uid]).then(async (response) => {
              if (response.length === 1) {
                parent_payment_method = response[0].parent_payment_method;
              }
            }).catch((error) => {
              // Payment method probably shows multi. Not the biggest issue
              console.log(error);
            });

            const formatted = new TransactionModel(response, parent_payment_method);
            await this.set(KEYS.PURCHASE, formatted);

            this.setLoading(false);

            return formatted;
          })
          .catch((error) => {
            if (!axios.isCancel(error))
              app.store.toasters.add({
                variant: 'error',
                title: `${t('purchases.failed-to-retrieve-with-id', {ns: 'store'})} '<strong>${id}</strong>'`,
                description: AcFormatErrorMessage(error),
              });

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      set = (target, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (AcIsUndefined(value)) return;

        return new Promise((resolve) => {
          this[target] = value;
          if (save) AcSaveState(target, value);
          resolve();
        });
      };

    @action
      setState = (target, property, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (!AcIsSet(property)) return;
        if (AcIsUndefined(value)) return;

        this[target][property] = value;
        if (save) AcSaveState(target, value);
      };

    @action
      reset = (target, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;

        return new Promise((resolve) => {
          this[target] = _default[target];
          if (save && AcIsNull(_default[target])) {
            AcRemoveState(target);
          } else if (save) {
            AcSaveState(target, _default[target]);
          }

          resolve();
        });
      };
}

export default PurchasesStore;
